import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  getLatestTaxComputations,
  getTaxPeriods,
  runTaxAction,
} from "../../Services/taxService";
import "./tax.css";

export default function VATCenter() {
  const { companyId } = useCompany();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [periodData, compData] = await Promise.all([
        getTaxPeriods(companyId, "VAT"),
        getLatestTaxComputations(companyId, "VAT"),
      ]);

      setPeriods(periodData || []);
      setRows(compData || []);

      setSelectedPeriod(
        (prev) => prev || (periodData?.length ? periodData[0].id : null)
      );
    } catch (err) {
      setMsg(err.message || "Failed to load VAT data");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRun() {
    if (!companyId || !selectedPeriod) return;
    setBusy(true);
    setMsg("");
    try {
      await runTaxAction({
        action: "run_vat",
        companyId,
        taxPeriodId: selectedPeriod,
      });
      setMsg("VAT computation completed");
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to run VAT computation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>VAT Center</h1>
          <p>Run VAT computations directly from posted journal entries.</p>
        </div>

        <div className="tax-actions">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="">Select Period</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.period_start} to {p.period_end}
              </option>
            ))}
          </select>

          <button
            className="tax-btn primary"
            onClick={handleRun}
            disabled={busy || !selectedPeriod}
          >
            {busy ? "Running..." : "Run VAT"}
          </button>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <div className="tax-panel">
        <h2>VAT Computations</h2>

        {rows.length === 0 ? (
          <div className="tax-empty">No VAT computations yet.</div>
        ) : (
          <div className="tax-table-wrap">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Output VAT</th>
                  <th>Input VAT Recoverable</th>
                  <th>VAT Payable</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const t = row.totals_json || {};
                  return (
                    <tr key={row.id}>
                      <td>{row.status}</td>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td>₦{Number(t.output_vat || 0).toLocaleString()}</td>
                      <td>
                        ₦{Number(t.input_vat_recoverable || 0).toLocaleString()}
                      </td>
                      <td>₦{Number(t.vat_payable || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
